import type { HttpTransportPort } from '@core/domain/ports/driven/HttpTransportPort.ts';
import { SYNC_CATALOG_GET_TIMEOUT_MS } from './syncCatalogTimeouts.ts';
import { withSyncFetchRetry } from './syncFetchRetry.ts';

const SYNC_GET_TIMEOUT_MS = SYNC_CATALOG_GET_TIMEOUT_MS;

function parseContentLengthHeader(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

async function resolveDownloadContentLength(
  url: string,
  token: string | null,
  transport: HttpTransportPort,
  headers: Record<string, string>,
  signal: AbortSignal | undefined,
): Promise<number | null> {
  try {
    const head = await transport.request(url, {
      method: 'HEAD',
      headers,
      ...(signal ? { signal } : {}),
    });
    if (!head.ok) return null;
    return parseContentLengthHeader(head.getHeader('Content-Length'));
  } catch {
    return null;
  }
}

/** GET JSON master-data bundle (participantes, proveedores, usuarios, puntos_entrega) for offline refresh. */
export async function getMasterDataUpdate(
  url: string,
  token: string | null,
  transport: HttpTransportPort,
  onProgress?: (loaded: number, total: number) => void,
): Promise<unknown> {
  return withSyncFetchRetry(async () => {
    const signal =
      typeof globalThis.AbortSignal !== 'undefined' && typeof globalThis.AbortSignal.timeout === 'function'
        ? globalThis.AbortSignal.timeout(SYNC_GET_TIMEOUT_MS)
        : undefined;

    const headers: Record<string, string> = {
      Accept: 'application/json, text/plain, */*',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const headContentLength = onProgress
      ? await resolveDownloadContentLength(url, token, transport, headers, signal)
      : null;

    const response = await transport.request(url, {
      method: 'GET',
      headers,
      onProgress: onProgress
        ? (loaded, total) => {
            const effectiveTotal = total > 0 ? total : (headContentLength ?? 0);
            onProgress(loaded, effectiveTotal);
          }
        : undefined,
      ...(signal ? { signal } : {}),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      const hint = body.trim() ? `: ${body.slice(0, 240)}` : '';
      throw new Error(`HTTP ${response.status}${hint}`);
    }
    return response.json() as Promise<unknown>;
  });
}
